import {
  Avatar,
  cn,
  Select as HeroSelect,
  SelectItem,
  SelectProps as HeroSelectProps,
} from '@heroui/react'
import { useState } from 'react'

interface Option {
  key: string
  label: string
  flag?: string
}

type CustomSelectProps = Omit<
  HeroSelectProps<Option>,
  'children' | 'selectedKeys' | 'onSelectionChange' | 'selectionMode' | 'onChange'
>

export interface SelectProps extends CustomSelectProps {
  label: string
  options: Option[]
  name?: string
  multiple?: boolean
  onChange?: (value: string | string[]) => void
  onBlur?: () => void
  classNameContainer?: string
  variant?: 'underlined' | 'flat' | 'faded' | 'bordered'
  classNames?: {
    base?: string
    label?: string
    trigger?: string
    value?: string
    selectorIcon?: string
    popoverContent?: string
    [key: string]: string | undefined
  }
  value?: string | string[]
  defaultValue?: string | string[]
  errorMessage?: string
  placeholder?: string
}

const Select = ({
  label,
  options,
  name,
  multiple,
  onChange,
  onBlur,
  classNameContainer,
  classNames,
  value,
  defaultValue,
  errorMessage,
  placeholder,
  variant,
  ...props
}: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [internalValue, setInternalValue] = useState(defaultValue)

  // Use value if provided (controlled), otherwise use internal state (uncontrolled)
  const currentValue = value !== undefined ? value : internalValue

  const selectedValues = Array.isArray(currentValue)
    ? currentValue.map(String)
    : currentValue
      ? [String(currentValue)]
      : []

  return (
    <div className={cn(' w-full', classNameContainer)}>
      <div
        aria-disabled={props.isDisabled}
        className="relative"
        role="input"
        tabIndex={props.isDisabled ? -1 : 0}
      >
        <HeroSelect
          {...props}
          isOpen={isOpen}
          label={label}
          labelPlacement="outside"
          name={name}
          placeholder={placeholder}
          radius="sm"
          selectedKeys={selectedValues.length > 0 ? new Set(selectedValues) : undefined}
          selectionMode={multiple ? 'multiple' : 'single'}
          variant="bordered"
          classNames={{
            label: [
              'text-gray-900',
              'dark:text-foreground',
              'group-data-[filled-within=true]:text-gray-900',
              'dark:group-data-[filled-within=true]:text-white',
              'group-data-[invalid=true]:text-danger',
              'dark:group-data-[invalid=true]:text-danger',
            ],
            trigger: [
              'bg-gray-50',
              'dark:bg-[#1e1e20]',
              'border-gray-200',
              'dark:border-default-600',
              'hover:bg-gray-100',
              'dark:hover:bg-[#1e1e20]/70',
              'group-data-[focus=true]:bg-gray-100',
              'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
              'group-data-[focus=true]:border-primary',
              'dark:group-data-[focus=true]:border-primary',
              '!cursor-text',
            ],
            ...classNames,
          }}
          onOpenChange={setIsOpen}
          onClose={() => {
            setIsOpen(false)
            onBlur?.()
          }}
          onSelectionChange={
            props.isDisabled
              ? undefined
              : (keys: any) => {
                  const newValues = Array.from(keys).map(String)
                  const finalValue = multiple ? newValues : newValues[0] || ''

                  // Update internal state if uncontrolled
                  if (value === undefined) {
                    setInternalValue(finalValue)
                  }

                  // Call onChange if provided
                  onChange?.(finalValue)
                }
          }
        >
          {options.map(option => (
            <SelectItem
              key={option.key}
              title={option.label}
              startContent={
                option.flag ? (
                  <Avatar
                    alt={option.label}
                    className="w-6 h-6"
                    src={`https://flagcdn.com/${option.flag}.svg`}
                  />
                ) : undefined
              }
            >
              {option.label}
            </SelectItem>
          ))}
        </HeroSelect>
      </div>
    </div>
  )
}

export default Select
